/**
 * UserGuide — fancy onboarding panel shown as its own tab on the landing page.
 * Role-aware (hides admin-only features for regular users).
 */
import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';

export default function UserGuide() {
  const { session } = useAuth();
  const isAdmin = session?.role === 'admin';
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      num: 1,
      title: 'Generate an API Key',
      desc: 'Go to Settings → Manage API and create an API key. You use it to pull data.',
      icon: '🔑',
      color: '#58a6ff',
    },
    {
      num: 2,
      title: 'Pull Data via the API',
      desc: 'Read the Docs tab for curl + Python examples. Pass your key in the X-API-Key header to fetch OHLCV, funding, open interest and more for any onboarded coin.',
      icon: '⚡',
      color: '#bc8cff',
    },
  ];

  const tabs = [
    {
      area: 'Home',
      name: 'Dashboard',
      desc: 'Your saved row tabs. Create, download, edit, delete Parquet files.',
      admin: false,
    },
    {
      area: 'Home',
      name: 'My Activity',
      desc: 'History of your creates, edits, downloads and deletes.',
      admin: false,
    },
    {
      area: 'Settings',
      name: 'Manage API',
      desc: 'Generate and revoke API keys for programmatic access.',
      admin: false,
    },
    {
      area: 'Settings',
      name: 'Docs',
      desc: 'Full API reference, curl examples, Python snippets.',
      admin: false,
    },
    {
      area: 'Settings',
      name: 'Architecture',
      desc: 'Coin registry, ingestion sources, feature coverage.',
      admin: true,
    },
    {
      area: 'Settings',
      name: 'Explorer',
      desc: 'Interactive heatmap + TradingView charts + order book depth.',
      admin: true,
    },
    {
      area: 'Settings',
      name: 'Validation',
      desc: 'Data quality checks and spike detection.',
      admin: true,
    },
    {
      area: 'Settings',
      name: 'Progress',
      desc: 'Live ingestion job progress across all sources.',
      admin: true,
    },
    {
      area: 'Settings',
      name: 'Activity Log',
      desc: 'System-wide audit log across all users.',
      admin: true,
    },
    {
      area: 'Settings',
      name: 'Users',
      desc: 'Manage user accounts and roles.',
      admin: true,
    },
  ];

  const visibleTabs = tabs.filter((t) => !t.admin || isAdmin);

  return (
    <div
      style={{
        position: 'relative',
        background:
          'linear-gradient(135deg, #0d1b2a 0%, #161b22 50%, #1b1530 100%)',
        border: '1px solid #30363d',
        borderRadius: 14,
        padding: 32,
        overflow: 'hidden',
      }}
    >
      {/* Decorative blur orbs */}
      <div
        style={{
          position: 'absolute',
          top: -80,
          right: -80,
          width: 280,
          height: 280,
          background:
            'radial-gradient(circle, rgba(88,166,255,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: -120,
          left: -120,
          width: 320,
          height: 320,
          background:
            'radial-gradient(circle, rgba(188,140,255,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Hero */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: 20,
              background: 'rgba(88,166,255,0.12)',
              border: '1px solid rgba(88,166,255,0.3)',
              color: '#58a6ff',
              fontSize: 11,
              fontWeight: 600,
              marginBottom: 14,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {isAdmin ? 'Admin Welcome' : 'Welcome aboard'}
          </div>
          <h1
            style={{
              color: '#e6edf3',
              fontSize: 30,
              fontWeight: 700,
              margin: '0 0 10px',
              background:
                'linear-gradient(90deg, #e6edf3 0%, #58a6ff 50%, #bc8cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Your Binance market data, one click away.
          </h1>
          <p
            style={{
              color: '#8b949e',
              fontSize: 15,
              margin: 0,
              maxWidth: 680,
              lineHeight: 1.6,
            }}
          >
            Pull OHLCV, funding rates, open interest, long/short ratios and
            order book snapshots across 31+ pairs — as JSON or ready-to-analyze
            Parquet files.
            {isAdmin
              ? ' As an admin, you also get the Explorer, Validation, Progress and Activity tools in Settings.'
              : ''}
          </p>
        </div>

        {/* Steps — horizontal stepper */}
        <div style={{ marginBottom: 28 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#8b949e',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 14,
            }}
          >
            Quick tour &mdash; 3 steps
          </div>

          {/* Step pills */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 18,
              flexWrap: 'wrap',
            }}
          >
            {steps.map((step, i) => (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                style={{
                  flex: '1 1 120px',
                  minWidth: 110,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: activeStep === i ? `${step.color}1a` : '#0d1117',
                  border: `1px solid ${activeStep === i ? step.color + '66' : '#30363d'}`,
                  color: activeStep === i ? step.color : '#8b949e',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: activeStep === i ? step.color : '#30363d',
                      color: activeStep === i ? '#0d1117' : '#8b949e',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {step.num}
                  </span>
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {step.title}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Active step detail */}
          <div
            style={{
              background: '#0d1117',
              border: `1px solid ${steps[activeStep].color}44`,
              borderRadius: 10,
              padding: 20,
              display: 'flex',
              gap: 16,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: `${steps[activeStep].color}22`,
                border: `1px solid ${steps[activeStep].color}66`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              {steps[activeStep].icon}
            </div>
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  color: '#e6edf3',
                  fontSize: 15,
                  fontWeight: 600,
                  margin: '0 0 6px',
                }}
              >
                {steps[activeStep].title}
              </h3>
              <p
                style={{
                  color: '#8b949e',
                  fontSize: 13,
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {steps[activeStep].desc}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: 'transparent',
                  border: '1px solid #30363d',
                  color: activeStep === 0 ? '#484f58' : '#c9d1d9',
                  cursor: activeStep === 0 ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                &#8592;
              </button>
              <button
                onClick={() =>
                  setActiveStep(Math.min(steps.length - 1, activeStep + 1))
                }
                disabled={activeStep === steps.length - 1}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  background: 'transparent',
                  border: '1px solid #30363d',
                  color:
                    activeStep === steps.length - 1 ? '#484f58' : '#c9d1d9',
                  cursor:
                    activeStep === steps.length - 1 ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                &#8594;
              </button>
            </div>
          </div>
        </div>

        {/* Tab map */}
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#8b949e',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: 14,
            }}
          >
            What&apos;s where
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 10,
            }}
          >
            {visibleTabs.map((t) => (
              <div
                key={`${t.area}-${t.name}`}
                style={{
                  background: '#0d1117',
                  border: '1px solid #21262d',
                  borderRadius: 8,
                  padding: 14,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#30363d';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#21262d';
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '1px 7px',
                      borderRadius: 4,
                      background: t.area === 'Home' ? '#388bfd22' : '#a371f722',
                      color: t.area === 'Home' ? '#58a6ff' : '#bc8cff',
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t.area}
                  </span>
                  <span
                    style={{ color: '#e6edf3', fontSize: 13, fontWeight: 600 }}
                  >
                    {t.name}
                  </span>
                  {t.admin && (
                    <span
                      style={{
                        color: '#d29922',
                        fontSize: 10,
                        marginLeft: 'auto',
                      }}
                    >
                      admin
                    </span>
                  )}
                </div>
                <div
                  style={{ color: '#8b949e', fontSize: 12, lineHeight: 1.5 }}
                >
                  {t.desc}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pro tips strip */}
        <div
          style={{
            background: 'rgba(63,185,80,0.06)',
            border: '1px solid rgba(63,185,80,0.25)',
            borderRadius: 10,
            padding: '14px 18px',
            display: 'flex',
            gap: 20,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              color: '#3fb950',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Pro Tips
          </div>
          {[
            'Date range is capped at 365 days per request.',
            'Responses cap at 10,000 rows per data type — check warnings.',
            'Coin symbols take the USDT suffix automatically.',
            'Parquet ZIP splits into spot_ohlcv + derivatives per coin.',
          ].map((tip, i) => (
            <div
              key={i}
              style={{
                color: '#c9d1d9',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: '#3fb950',
                  display: 'inline-block',
                }}
              />
              {tip}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
